'use client';

import { ReactElement, useMemo } from 'react';
import { addDays, format, getISODay, parse } from 'date-fns';
import { css } from '@bts-web/utils-style-engine';
import { SavingsPlanFrequency } from '@bts-web/data-layer/server';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  BaseDrawerProps,
  useDrawerMetadata,
} from '../../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { Drawer } from '../../../../../../common/components/Drawer/Drawer';
import {
  FilledDismiss,
  RegularCheckmark,
} from '../../../../../../common/svgIcons';
import { getYmdForNthDayFromToday } from '../../../../utils/dateUtils';
import { getWeekDayIndexFromYmd } from './WeekDayPicker';
import {
  SP_WEEKLY_FREQUENCY,
  SP_BIWEEKLY_FREQUENCY,
  SP_DAILY_FREQUENCY,
  SP_MONTHLY_FREQUENCY,
} from '../../../types';

const YMD_STORAGE_FORMAT = 'yyyy-MM-dd';

export const getNextRecurrenceForFrequencyItemInDrawer = (
  targetFrequency: SavingsPlanFrequency,
  currentSelectedRecurrenceDate: string,
  currentSelectedFrequency: SavingsPlanFrequency,
): string => {
  const today = new Date();

  if (targetFrequency === SP_DAILY_FREQUENCY) {
    return format(today, 'dd/MM/yyyy');
  }

  if (
    targetFrequency === SP_WEEKLY_FREQUENCY ||
    targetFrequency === SP_BIWEEKLY_FREQUENCY
  ) {
    const { minimumCalendarDaysBeforeExecution = 4 } = getAppConfig();

    const isWeeklyLike = (f: SavingsPlanFrequency) =>
      f === SP_WEEKLY_FREQUENCY || f === SP_BIWEEKLY_FREQUENCY;

    const isoDay = isWeeklyLike(currentSelectedFrequency)
      ? getWeekDayIndexFromYmd(currentSelectedRecurrenceDate)
      : 1; // default to Monday when switching from a non-weekly frequency

    const todayIsoDay = getISODay(today);

    let daysUntil = isoDay - todayIsoDay;

    if (daysUntil < 0) daysUntil += 7;

    if (daysUntil < minimumCalendarDaysBeforeExecution) daysUntil += 7;

    const base = addDays(today, daysUntil);

    return format(base, 'dd/MM/yyyy');
  }

  if (targetFrequency === SP_MONTHLY_FREQUENCY) {
    if (currentSelectedFrequency === SP_MONTHLY_FREQUENCY) {
      return format(
        parse(currentSelectedRecurrenceDate, YMD_STORAGE_FORMAT, today),
        'dd/MM/yyyy',
      );
    }

    const { minimumCalendarDaysBeforeExecution = 4 } = getAppConfig();

    return format(
      parse(
        getYmdForNthDayFromToday(minimumCalendarDaysBeforeExecution),
        YMD_STORAGE_FORMAT,
        today,
      ),
      'dd/MM/yyyy',
    );
  }

  return '';
};

type FrequencySelectDrawerProps = BaseDrawerProps & {
  frequencyOptions: SavingsPlanFrequency[];
  currentSelectedFrequency: SavingsPlanFrequency;
  onSelect: (freq: SavingsPlanFrequency) => void;
  translations: Partial<Record<SavingsPlanFrequency, string>> & {
    frequency: string;
    nextRecurrence?: string;
  };
  currentSelectedRecurrenceDate: string;
};

export const FrequencySelectDrawer = ({
  frequencyOptions,
  currentSelectedFrequency,
  onSelect,
  translations,
  currentSelectedRecurrenceDate,
}: FrequencySelectDrawerProps): ReactElement => {
  const { position, closeThisDrawer } = useDrawerMetadata();

  const isBottomDrawer = position === 'bottom';

  const nextRecurrenceDates = useMemo(
    () =>
      Object.fromEntries(
        frequencyOptions.map((frequency) => [
          frequency,
          getNextRecurrenceForFrequencyItemInDrawer(
            frequency,
            currentSelectedRecurrenceDate,
            currentSelectedFrequency,
          ),
        ]),
      ) as Partial<Record<SavingsPlanFrequency, string>>,
    [currentSelectedRecurrenceDate, frequencyOptions, currentSelectedFrequency],
  );

  return (
    <Drawer hideDefaultHeader>
      {isBottomDrawer && (
        <div
          className={css({
            display: 'flex',
            justifyContent: 'center',
            pt: 'extra_small_2',
            pb: 'extra_small',
          })}
        >
          <span
            className={css({
              maxWidth: '40px',
              height: '2px',
              flex: 1,
              backgroundColor: 'neutrals.fill_tertiary',
              borderEndEndRadius: 'smallBottomRight',
              borderEndStartRadius: 'smallTopRight',
              borderStartStartRadius: 'smallTopLeft',
              borderStartEndRadius: 'smallBottomLeft',
            })}
          />
        </div>
      )}

      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          px: 'medium',
          py: 'small',
          borderBottom: '1px solid',
          borderBottomColor: 'neutrals.divider',
        })}
      >
        <div className={css({ flex: 1 })} />

        <span
          className={css({
            fontSize: 'body.medium_medium',
            fontWeight: 'body.medium_medium',
            color: 'neutrals.text_primary',
          })}
        >
          {translations.frequency}
        </span>

        <div
          className={css({
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
          })}
        >
          <button
            type="button"
            onClick={closeThisDrawer}
            aria-label="Close"
            className={css({ color: 'neutrals.text_primary' })}
          >
            <FilledDismiss size={24} />
          </button>
        </div>
      </div>

      <ul>
        {frequencyOptions.map((freq) => {
          const isSelected = currentSelectedFrequency === freq;

          return (
            <li key={freq}>
              <button
                type="button"
                onClick={() => {
                  onSelect(freq);

                  closeThisDrawer();
                }}
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  px: 'medium',
                  py: 'medium',
                  ...(isSelected
                    ? { backgroundColor: 'neutrals.fill_quinary' }
                    : {}),
                })}
              >
                <div
                  className={css({
                    display: 'flex',
                    textAlign: 'start',
                    flexDirection: 'column',
                    gap: 'extra_small_3',
                  })}
                >
                  <span
                    className={css({
                      fontSize: 'title.medium_medium',
                      fontWeight: 'title.medium_medium',
                      color: 'neutrals.text_primary',
                    })}
                  >
                    {translations[freq]}
                  </span>

                  {translations.nextRecurrence && (
                    <span
                      className={css({
                        fontSize: 'caption.small_light',
                        fontWeight: 'caption.small_light',
                        color: 'neutrals.text_secondary',
                      })}
                    >
                      {translations.nextRecurrence}: {nextRecurrenceDates[freq]}
                    </span>
                  )}
                </div>

                {isSelected && <RegularCheckmark size={16} />}
              </button>
            </li>
          );
        })}
      </ul>
    </Drawer>
  );
};
